'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { Button, Icon, IconComponent } from '@graftcode/design-system'

import { useIsLocalGatewayConfigurationMode } from '@/core/hooks/useIsLocalGatewayConfigurationMode'
import { truncateGatewayDisplayName } from '@/core/utils/truncateGatewayDisplayName'

import { GatewayConnectionSettings } from './GatewayConnectionSettings'

import styles from './GatewaySelector.module.scss'

const COPY_FEEDBACK_MS = 2000

interface GatewaySelectorProps {
  gatewayName: string
  loading: boolean
  onRefetchGateway: () => Promise<unknown>
}

export function GatewaySelector({ gatewayName, loading, onRefetchGateway }: GatewaySelectorProps) {
  const [copied, setCopied] = useState(false)
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showSettings = useIsLocalGatewayConfigurationMode()

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current)
      }
    },
    []
  )

  const handleCopy = useCallback(() => {
    if (loading || !gatewayName) return

    navigator.clipboard.writeText(gatewayName).then(() => {
      setCopied(true)

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current)
      }

      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false)
        copyTimeoutRef.current = null
      }, COPY_FEEDBACK_MS)
    })
  }, [gatewayName, loading])

  if (loading && !showSettings) {
    return null
  }

  const displayText = truncateGatewayDisplayName(gatewayName || 'Gateway')

  return (
    <div className={styles.wrapper}>
      <IconComponent icon={Icon.DSCodeGateway} className={styles.icon} />
      <span className={styles.name}>{displayText}</span>
      <div>
        <Button
          iconOnly
          icon={copied ? Icon.CopyFilled : Icon.CopyRegular}
          variant="ghost"
          size="s"
          className={styles.copyButton}
          onClick={handleCopy}
          aria-label={`Copy ${gatewayName}`}
          title={`Copy: ${gatewayName}`}
        />
        {showSettings ? <GatewayConnectionSettings onRefetchGateway={onRefetchGateway} variant="icon" /> : null}
      </div>
    </div>
  )
}
